import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { downloadReport } from "./exportHelpers";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { FileText, FileDown, FileSpreadsheet, Loader2 } from "lucide-react";

const C = { burg: "#8B1E1E", gold: "#C49A4A", text: "#3B2722", muted: "#8B7355", border: "#EFE4D0", canvas: "#FAF6EC" };
const REPORTS = [
  { key: "revenue", label: "Revenue & Collections" },
  { key: "expenses", label: "Departmental Expenses" },
  { key: "petty-cash", label: "Petty Cash Register" },
  { key: "bad-damaged", label: "Bad & Damaged" },
  { key: "inventory", label: "Inventory & Issuances" },
];
const n = (v) => (v || v === 0 ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "");
const thisMonth = () => new Date().toISOString().slice(0, 7);

function GridTable({ dayHeaders, rows, footer }) {
  return (
    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: C.border }} data-testid="grid-table">
      <table className="text-xs tabular-nums" style={{ minWidth: "100%" }}>
        <thead>
          <tr style={{ background: C.burg, color: "#fff" }}>
            {dayHeaders.map((h, i) => (
              <th key={i} className={`px-2 py-2 font-semibold whitespace-nowrap ${i === 0 ? "text-left sticky left-0 z-10" : "text-right"}`}
                style={i === 0 ? { background: C.burg } : {}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? C.canvas : "#fff" }}>
              {r.map((c, ci) => (
                <td key={ci} className={`px-2 py-1.5 whitespace-nowrap ${ci === 0 ? "text-left font-medium sticky left-0" : "text-right"}`}
                  style={ci === 0 ? { color: C.text, background: ri % 2 ? C.canvas : "#fff" } : { color: C.muted }}>{c}</td>
              ))}
            </tr>
          ))}
          {footer && (
            <tr style={{ background: "#FDF6E3", fontWeight: 700 }}>
              {footer.map((c, ci) => (
                <td key={ci} className={`px-2 py-2 whitespace-nowrap ${ci === 0 ? "text-left sticky left-0" : "text-right"}`}
                  style={{ color: C.burg, background: "#FDF6E3", borderTop: `1px solid ${C.burg}` }}>{c}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportsHub() {
  const [report, setReport] = useState("revenue");
  const [period, setPeriod] = useState(thisMonth());
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("all");
  const [category, setCategory] = useState("all");
  const [group, setGroup] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { period };
    if (loc !== "all") params.location_id = loc;
    if (category !== "all" && ["bad-damaged", "inventory"].includes(report)) params.category = category;
    if (group !== "all" && report === "inventory") params.group = group;
    const path = { revenue: "/reports/hub/revenue", expenses: "/reports/hub/expenses", "petty-cash": "/reports/hub/petty-cash", "bad-damaged": "/reports/hub/bad-damaged", inventory: "/reports/hub/inventory" }[report];
    api.get(path, { params }).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [report, period, loc, category, group]);
  useEffect(() => { load(); }, [load]);

  const doExport = (fmt) => {
    const params = { period };
    if (loc !== "all") params.location_id = loc;
    if (category !== "all" && ["bad-damaged", "inventory"].includes(report)) params.category = category;
    if (group !== "all" && report === "inventory") params.group = group;
    downloadReport(`/reports/hub/exports/${report}.${fmt}`, `${report}-${period}.${fmt}`, params);
  };

  const days = data?.days || 0;
  const dayHead = (first, tail = []) => [first, ...Array.from({ length: days }, (_, i) => String(i + 1)), ...tail];

  const renderBody = () => {
    if (loading) return <div className="flex items-center gap-2 p-8 text-sm" style={{ color: C.muted }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;
    if (!data) return <div className="p-8 text-sm" style={{ color: C.muted }}>No data for this period.</div>;
    // Guard against a tab switch rendering against stale data of a different shape
    const expectsRows = report !== "revenue";
    if (report === "revenue" && !data.channels) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;
    if (expectsRows && !Array.isArray(data.rows)) return <div className="p-8 text-sm" style={{ color: C.muted }}>Loading…</div>;

    if (report === "revenue") {
      const rows = data.channels.map((c) => [c.label, ...Array.from({ length: days }, (_, i) => n(data.matrix[c.key][i + 1])), n(data.channel_total[c.key])]);
      const footer = ["Daily Total", ...Array.from({ length: days }, (_, i) => n(data.daily_total[i + 1])), n(data.grand_total)];
      return <><Summary items={[["Monthly Revenue", n(data.grand_total)]]} /><GridTable dayHeaders={dayHead("Channel", ["Total"])} rows={rows} footer={footer} /></>;
    }
    if (report === "expenses") {
      const rows = data.rows.map((r) => [r.label, ...Array.from({ length: days }, (_, i) => n(r.values[i + 1])), n(r.total)]);
      const footer = ["Total", ...Array.from({ length: days }, (_, i) => n(data.daily_total[i + 1])), n(data.grand_total)];
      return <><Summary items={[["Monthly Expenses", n(data.grand_total)]]} /><GridTable dayHeaders={dayHead("Expense Line", ["Total"])} rows={rows} footer={footer} /></>;
    }
    if (report === "petty-cash") {
      const rows = data.rows.map((r) => [r.date, r.pvc || "", r.description, n(r.cash_in), n(r.cash_out), n(r.running)]);
      return <><Summary items={[["Opening", n(data.opening)], ["Cash In", n(data.total_in)], ["Cash Out", n(data.total_out)], ["Closing", n(data.closing)]]} />
        <GridTable dayHeaders={["Date", "PVC No.", "Description", "Cash In", "Cash Out", "Balance"]} rows={rows}
          footer={["", "", "Total", n(data.total_in), n(data.total_out), n(data.closing)]} /></>;
    }
    if (report === "bad-damaged") {
      const rows = data.rows.map((r) => [r.name, ...Array.from({ length: days }, (_, i) => (r.values[i + 1] || "")), r.total_qty || "", n(r.total_cost)]);
      return <><CategoryFilter cats={data.categories} value={category} onChange={setCategory} />
        <Summary items={[["Total B&D Cost", n(data.total_bd_cost)], ["Total Sales", n(data.total_sales)], ["Loss-to-Sales", `${data.loss_to_sales_pct}%`]]} />
        <GridTable dayHeaders={dayHead("Product", ["Qty", "Cost"])} rows={rows} /></>;
    }
    if (report === "inventory") {
      const rows = data.rows.map((r) => [r.name, r.uom, r.open_q, n(r.open_v), r.pur_q, n(r.pur_v), r.iss_q, n(r.iss_v), r.close_q, n(r.close_v)]);
      return <><div className="flex flex-wrap items-center gap-4">
          <StockGroupFilter value={group} onChange={setGroup} />
          <CategoryFilter cats={data.categories} value={category} onChange={setCategory} />
        </div>
        <GridTable dayHeaders={["Item", "UOM", "Open Qty", "Open Val", "Purch Qty", "Purch Val", "Issued Qty", "Issued Val", "Close Qty", "Close Val"]} rows={rows} /></>;
    }
    return null;
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="reports-hub">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border p-3" style={{ borderColor: C.border, background: "#FFFDF9" }}>
        <div><label className="text-[10px] font-bold tracking-widest block mb-1" style={{ color: C.muted }}>MONTH</label>
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-10 w-44" data-testid="rh-period" /></div>
        <div><label className="text-[10px] font-bold tracking-widest block mb-1" style={{ color: C.muted }}>BRANCH</label>
          <Select value={loc} onValueChange={setLoc}><SelectTrigger className="h-10 w-48" data-testid="rh-branch"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Branches</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => doExport("csv")} data-testid="rh-export-csv"><FileDown size={15} className="mr-1" /> CSV</Button>
        <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="rh-export-xlsx" style={{ borderColor: C.gold, color: C.burg }}><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
        <Button onClick={() => doExport("pdf")} style={{ background: C.burg }} className="text-white" data-testid="rh-export-pdf"><FileText size={15} className="mr-1" /> PDF</Button>
      </div>

      <Tabs value={report} onValueChange={(v) => { setReport(v); setCategory("all"); setGroup("all"); setData(null); }}>
        <TabsList className="flex-wrap h-auto">
          {REPORTS.map((r) => <TabsTrigger key={r.key} value={r.key} data-testid={`rh-tab-${r.key}`}>{r.label}</TabsTrigger>)}
        </TabsList>
        {REPORTS.map((r) => <TabsContent key={r.key} value={r.key} className="space-y-3">{report === r.key && renderBody()}</TabsContent>)}
      </Tabs>
    </div>
  );
}

function Summary({ items }) {
  return (
    <div className="flex flex-wrap gap-3">
      {items.map(([label, val], i) => (
        <div key={i} className="rounded-xl border px-4 py-2" style={{ borderColor: C.border, background: "#FFFDF9" }}>
          <div className="text-[10px] font-bold tracking-widest" style={{ color: C.muted }}>{label.toUpperCase()}</div>
          <div className="font-brand font-bold text-lg" style={{ color: C.burg }}>{val}</div>
        </div>
      ))}
    </div>
  );
}

function CategoryFilter({ cats, value, onChange }) {
  if (!cats || !cats.length) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs" style={{ color: C.muted }}>Category:</span>
      <Select value={value} onValueChange={onChange}><SelectTrigger className="h-9 w-52" data-testid="rh-category"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
    </div>
  );
}

const STOCK_GROUPS = [
  { key: "general", label: "General Items" }, { key: "drinks", label: "Drinks & Beverages" },
  { key: "gelato", label: "Gelato" }, { key: "food", label: "Food" }, { key: "cake", label: "Cake" },
];

function StockGroupFilter({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold" style={{ color: C.muted }}>Stock Group:</span>
      <Select value={value} onValueChange={onChange}><SelectTrigger className="h-9 w-52" data-testid="rh-stock-group"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Stock</SelectItem>{STOCK_GROUPS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}</SelectContent></Select>
    </div>
  );
}